import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import type { CareTeamView } from '@/api/types';
import { useApi } from '@/api/useApi';
import {
  Avatar,
  Button,
  Caption,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Score,
  Tag,
  TextArea,
  TextInput,
  ValueBar,
} from '@/components/ui';
import { formatDate, formatRelative } from '@/lib/format';

/**
 * Screen 02 — one client, several caregivers.
 *
 * The question this screen answers is whether a client's experience is
 * consistent across the people who serve them. That is only askable once
 * scores are attributed to the caregiver on the rated visit — which is what
 * lets a dip be traced to handover rather than to a person.
 */
export function CareTeamPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useApi<CareTeamView>(
    `/agency/clients/${id}/care-team`,
  );
  const [coaching, setCoaching] = useState<{ id: string; name: string } | null>(null);

  if (loading) return <Loading />;
  if (error)
    return (
      <Page>
        <ErrorState message={error.message} onRetry={reload} />
      </Page>
    );
  if (!data) return null;

  const { client, consistency } = data;
  const lowest = consistency.lowest;

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/clients">Clients</Link>
            <span aria-hidden="true">/</span>
            <span>{client.name}</span>
          </nav>
        }
        title={client.full_name}
        subtitle={[client.location, client.service_status, `${data.care_team.length} caregivers`]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <Button variant="secondary" onClick={() => window.location.assign(`/clients/${id}/plan`)}>
              Open care plan{data.current_plan_version ? ` v${data.current_plan_version}` : ''}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.location.assign(`/clients/${id}/schedule`)}
            >
              Schedule &amp; caregivers
            </Button>
            <Button onClick={() => window.location.assign(`/clients/${id}`)}>
              Client record
            </Button>
          </>
        }
      />

      <div className="split-even">
        {/* ------------------------------------------------ care team */}
        <div className="stack stack--tight">
          <Card variant="flush">
            <CardHead title="Care team · last 30 days" />
            <div>
              {data.care_team.map((member) => (
                <div
                  key={member.id}
                  className="card"
                  style={{
                    border: 0,
                    borderBottom: '1px solid var(--rule-faint)',
                    // Earned by two facts together: an unacknowledged current
                    // plan and a visit imminent.
                    background: member.at_risk ? 'var(--melon-tint)' : undefined,
                    borderLeft: member.at_risk ? '3px solid var(--melon)' : undefined,
                  }}
                >
                  <div className="row row--top row--between">
                    <div className="row row--top">
                      <Avatar person={member} size="xl" />
                      <div>
                        <div className="cell-title">{member.name}</div>
                        <div className="cell-sub">
                          {member.visit_share}
                          {member.plan_version ? ` · plan v${member.plan_version}` : ''}
                          {member.plan_acknowledged
                            ? ` acknowledged ${formatDate(member.plan_acknowledged_at)}`
                            : ' not acknowledged'}
                        </div>
                        <div className="row row--wrap" style={{ gap: 'var(--s-6)', marginTop: 'var(--s-6)' }}>
                          {member.tags.map((tag) => (
                            <Tag
                              key={tag.label}
                              label={tag.label}
                              tone={tag.tone as 'positive' | 'info' | 'neutral'}
                            />
                          ))}
                          {member.at_risk ? (
                            <Tag
                              label={
                                member.next_visit_at
                                  ? `Visit ${formatRelative(member.next_visit_at)}`
                                  : 'Visit imminent'
                              }
                              tone="urgent"
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Score
                        score={member.score.average}
                        suppressed={member.score.suppressed}
                        label={member.score.label}
                        responses={member.score.response_count}
                      />
                      <div style={{ marginTop: 'var(--s-6)' }}>
                        <Button
                          variant="ghost"
                          onClick={() => setCoaching({ id: member.id, name: member.name })}
                        >
                          Coaching note
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Caption>{data.visibility_note}</Caption>
        </div>

        {/* ------------------------------------------------ consistency */}
        <div className="stack stack--tight">
          <Card>
            <div className="stack">
              <h3>Where the experience differs</h3>

              {consistency.gap_is_meaningful && lowest ? (
                <Notice tone="attention">
                  Families rate <strong>{lowest.label}</strong> lower than the other values across
                  every caregiver on this team. A gap that appears with all three is more likely to
                  sit in handover than in one person&apos;s care.
                </Notice>
              ) : (
                <Caption>
                  No value stands out across the team. Where a gap is not meaningful, this panel
                  says so rather than inventing a pattern.
                </Caption>
              )}

              <div className="stack stack--tight">
                {consistency.values.map((value) => (
                  <ValueBar
                    key={value.field}
                    label={value.label}
                    score={value.score}
                    suppressed={value.suppressed}
                    responses={value.responses}
                    isLowest={lowest?.field === value.field && consistency.gap_is_meaningful}
                  />
                ))}
              </div>

              {consistency.gap_is_meaningful && lowest ? (
                <div className="row row--wrap">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setCoaching({
                        id: data.care_team[0]?.id ?? '',
                        name: data.care_team[0]?.name ?? '',
                      })
                    }
                  >
                    Open coaching note
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => window.location.assign(`/clients/${id}/plan`)}
                  >
                    Add handover step to plan
                  </Button>
                </div>
              ) : null}

              <Caption>
                {consistency.responses} responses across {consistency.visits} visits.{' '}
                {consistency.note} Values with fewer than {data.threshold} responses show no score.
              </Caption>
            </div>
          </Card>
        </div>
      </div>

      {coaching ? (
        <CoachingModal
          clientId={id!}
          caregiver={coaching}
          suggestedSubject={
            consistency.gap_is_meaningful && lowest ? `Handover — ${lowest.label}` : ''
          }
          onClose={() => setCoaching(null)}
          onSaved={() => {
            setCoaching(null);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function CoachingModal({
  clientId,
  caregiver,
  suggestedSubject,
  onClose,
  onSaved,
}: {
  clientId: string;
  caregiver: { id: string; name: string };
  suggestedSubject: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState(suggestedSubject);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      await api.post(`/agency/clients/${clientId}/coaching-notes`, {
        caregiver_id: caregiver.id,
        subject: subject.trim(),
        body: body.trim(),
      });
      onSaved();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Coaching note · ${caregiver.name}`} onClose={onClose}>
      <div className="stack">
        <Notice tone="info" title="Recorded as coaching">
          Coaching is kept separately from any disciplinary process. A low score opens a
          conversation; it is not evidence against someone.
        </Notice>

        {problem ? (
          <Notice tone="urgent" title="Could not save">
            {problem}
          </Notice>
        ) : null}

        <Field label="Subject">
          {(props) => (
            <TextInput
              {...props}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              autoFocus
            />
          )}
        </Field>

        <Field label="What you observed, and what you agreed">
          {(props) => (
            <TextArea {...props} value={body} onChange={(event) => setBody(event.target.value)} />
          )}
        </Field>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} busy={busy} disabled={!subject.trim() || !body.trim()}>
            Save coaching note
          </Button>
        </div>
      </div>
    </Modal>
  );
}
